import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { BRAND_CONTEXT_CATEGORIES, BrandLearning } from "@/types/brand-context";
import { CategorySelect } from "./CategorySelect";
import { FIELD_LABEL_CLASS, TagsInput } from "./TagsInput";
import { parseTags } from "./format";

interface LearningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  learning?: BrandLearning | null;
  isPending?: boolean;
  onSubmit: (input: { title: string; body: string; category: string; tags: string[] }) => void;
}

export function LearningDialog({ open, onOpenChange, learning, isPending = false, onSubmit }: LearningDialogProps) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState<string>(BRAND_CONTEXT_CATEGORIES[0]);
  const [tagsText, setTagsText] = useState("");

  useEffect(() => {
    if (!open) return;
    setTitle(learning?.title ?? "");
    setBody(learning?.body ?? "");
    setCategory(learning?.category ?? BRAND_CONTEXT_CATEGORIES[0]);
    setTagsText(learning?.tags.join(", ") ?? "");
  }, [open, learning]);

  const isEditing = !!learning;
  const canSubmit = title.trim() !== "" && body.trim() !== "";

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({ title: title.trim(), body: body.trim(), category, tags: parseTags(tagsText) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Entry" : "Add a Learning"}</DialogTitle>
          <DialogDescription>
            Capture a takeaway, insight, or guardrail. These short notes feed directly into how
            Signal AI writes copy and prioritizes tests for this brand.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className={FIELD_LABEL_CLASS}>Title</Label>
            <Input
              placeholder="Avoid medical claims in copy"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className={FIELD_LABEL_CLASS}>Learning</Label>
            <Textarea
              placeholder="What did you learn? Include context, source, and how it should be applied."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="mt-1.5 min-h-[140px]"
            />
          </div>
          <div>
            <Label className={FIELD_LABEL_CLASS}>Category</Label>
            <CategorySelect value={category} onValueChange={setCategory} className="mt-1.5" />
          </div>
          <TagsInput value={tagsText} onChange={setTagsText} placeholder="reviews, cvr, messaging" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isPending}>
            {isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {isEditing ? "Save Changes" : "Save Learning"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
